/**
 * Onboarding — who has woken, and how much of Lanternfall they can see.
 *
 * Owner brief (2026-09-04): "it's super overwhelming at the lanternfall screen. In World of
 * Warcraft all you see is a character/race/skin tone/hair selection at the beginning and once you
 * get in the game you're in a starter/tutorial zone before growing and advancing into the bigger
 * zones."
 *
 * Two pure functions and one table, in the shared package because the client and the server have
 * to agree about them:
 *
 *   - `isOnboarded` decides Waking-screen-or-hub. It is deliberately generous: a calling already
 *     chosen, or a single extract on the ledger, counts as woken, so nobody who has already
 *     played is sent back through a tutorial written for somebody who has not.
 *   - `unlockedTabs` decides what the hub draws. Every input is server-owned progression data —
 *     the client never decides that a tab is unlocked, it only reads the answer off the ledger it
 *     was handed. Nothing here is a permission check: hiding a tab is presentation, and the
 *     routes behind those tabs are as authoritative as they ever were.
 *
 * The list is ordered the way the hub draws it, so callers can render it directly.
 */

import { DEFAULT_MAP_ID } from './constants.js';

/**
 * Every tab the hub can show. The client's `TabId` is this union — the hub's labels, icons and
 * views are all keyed by it — so a tab cannot exist in one place and be unknown to the gate.
 */
export const HUB_TAB_IDS = [
  'deploy',
  'calling',
  'skills',
  'stash',
  'ground',
  'workbench',
  'vendor',
  'contracts',
  'marks',
  'terminal',
  'roadmap',
  'changelog',
  'choir',
  'settings',
] as const;
export type HubTabId = (typeof HUB_TAB_IDS)[number];

/**
 * The four rooms a Waker always has: somewhere to go, something to carry, who they are, and the
 * settings. Everything else in Lanternfall is earned.
 */
export const ALWAYS_TABS: readonly HubTabId[] = ['calling', 'stash', 'deploy', 'settings'];

/**
 * The reading room: always reachable, never in the Town strip. These live behind the strip's
 * MORE chevron so a first-time player counts four places in town, not eight.
 */
export const OVERFLOW_TABS: readonly HubTabId[] = ['terminal', 'roadmap', 'changelog', 'choir'];

/** Everything `unlockedTabs` needs, all of it read off `/api/progress`. */
export interface OnboardingProgress {
  level: number;
  wick: number;
  skillPoints: number;
  /** The progression counter map (`Counter` keys → value). */
  counters: Readonly<Record<string, number>>;
  /** `CallingId`, or 0 / null when nobody has been chosen yet. */
  calling: number | null;
  /** The stash holds at least one crafting material. */
  hasMaterials: boolean;
}

/** The shape a caller can hand in when it only knows part of the ledger. */
export type PartialProgress = Partial<OnboardingProgress>;

function counter(p: PartialProgress, key: string): number {
  return p.counters?.[key] ?? 0;
}

/**
 * Has this account finished The Waking?
 *
 * True when the tutorial was actually walked out of (`tutorial_done`), and — the grandfather
 * rule — also true for anyone who was already playing before The Waking existed: a Calling on the
 * record, or one extract, means they have been through the door already. Both halves live here
 * rather than in the client so the hub and the room never disagree about who is new.
 */
export function isOnboarded(p: PartialProgress): boolean {
  if (counter(p, 'tutorial_done') > 0) return true;
  if (counter(p, 'extracts') > 0) return true;
  return typeof p.calling === 'number' && p.calling > 0;
}

/** The opposite reading, named the way the call sites read: show The Waking screen. */
export function needsWaking(p: PartialProgress): boolean {
  return !isOnboarded(p);
}

/**
 * One row of the unlock ladder: the tab, what opens it, and the line the toast says when it does.
 * Exported so the hub can explain a locked tab without restating the rule.
 */
export interface TabUnlock {
  tab: HubTabId;
  /** One clause, present tense: what the player did to open it. */
  reason: string;
  test: (p: OnboardingProgress) => boolean;
}

/**
 * The ladder, in the order tabs light up. Tuned so a first deploy opens Ground and the Workbench
 * within a raid or two of each other, and Marks/Contracts wait until there is a reason to read
 * them. Wick is a *lifetime* balance test, not an earning test: a player who has spent down to
 * zero keeps the Vendor.
 */
export const TAB_UNLOCKS: readonly TabUnlock[] = [
  { tab: 'ground', reason: 'more than one ground to stand on', test: (p) => p.level >= 2 },
  {
    tab: 'workbench',
    reason: 'salvage worth working',
    test: (p) => p.hasMaterials || p.level >= 2,
  },
  { tab: 'skills', reason: 'a point to spend', test: (p) => p.skillPoints >= 1 || p.level >= 2 },
  { tab: 'vendor', reason: 'Wick enough to trade', test: (p) => p.wick >= 50 },
  { tab: 'marks', reason: 'a record worth keeping', test: (p) => p.level >= 3 },
  { tab: 'contracts', reason: 'the Compact asking for you', test: (p) => p.level >= 4 },
];

const DEFAULTS: OnboardingProgress = {
  level: 1,
  wick: 0,
  skillPoints: 0,
  counters: {},
  calling: 0,
  hasMaterials: false,
};

export function fillProgress(p: PartialProgress): OnboardingProgress {
  return {
    level: p.level ?? DEFAULTS.level,
    wick: p.wick ?? DEFAULTS.wick,
    skillPoints: p.skillPoints ?? DEFAULTS.skillPoints,
    counters: p.counters ?? DEFAULTS.counters,
    calling: p.calling ?? DEFAULTS.calling,
    hasMaterials: p.hasMaterials ?? DEFAULTS.hasMaterials,
  };
}

/**
 * The tabs this account may see, in hub order.
 *
 * Always includes the four `ALWAYS_TABS` and the four `OVERFLOW_TABS` (the reading room is never
 * locked — it is only moved out of the strip). Everything else has to be earned off `TAB_UNLOCKS`.
 */
export function unlockedTabs(progress: PartialProgress): HubTabId[] {
  const p = fillProgress(progress);
  const open = new Set<HubTabId>([...ALWAYS_TABS, ...OVERFLOW_TABS]);
  for (const rule of TAB_UNLOCKS) if (rule.test(p)) open.add(rule.tab);
  return HUB_TAB_IDS.filter((id) => open.has(id));
}

/** The tabs that are earned but not yet open, for copy that wants to promise something. */
export function lockedTabs(progress: PartialProgress): HubTabId[] {
  const open = new Set(unlockedTabs(progress));
  return TAB_UNLOCKS.map((r) => r.tab).filter((id) => !open.has(id));
}

/** The line a newly opened tab announces itself with. */
export function unlockReason(tab: HubTabId): string {
  return TAB_UNLOCKS.find((r) => r.tab === tab)?.reason ?? 'Lanternfall opens';
}

/**
 * Until Ground unlocks, Deploy has exactly one answer and does not ask the question. This is the
 * ground it preselects — the same id the Ground tab would have offered first, which since v3 is
 * the city (`DEFAULT_MAP_ID`) and not the wreck.
 */
export const STARTER_GROUND_ID: string = DEFAULT_MAP_ID;

/** The map The Waking runs on, and the deploy mode that opens a private room for it. */
export const TUTORIAL_MAP_ID = 'yard';
export const TUTORIAL_MODE = 'tutorial';

/** What extracting from The Waking is worth. Granted once, by the server, on the way out. */
export const TUTORIAL_EXTRACT_WICK = 40;
export const TUTORIAL_MARK_ID = 'm_first_light';
